import { useMemo } from "react";

import NotFoundPage from "components/error/NotFoundPage";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import Box from "ds/components/Box";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageWorkerPool } from "hooks/useAnalytics/pages/workerPool";
import useErrorHandle from "hooks/useErrorHandle";
import { isSaasDistribution } from "utils/distribution";

import PrivateWorkersTierInfo from "../PrivateWorkersTierInfo";
import { showWorkerPoolFormDrawer } from "./CreateWorkerPool";
import WorkerPoolsEmpty from "./Empty";
import WorkerPoolsFiltersLayout from "./FiltersLayout";
import WorkerPoolsPageLayout from "./PageLayout";
import { WORKER_POOLS_TABLE_CELLS } from "./Table/Cells";
import { WORKER_POOLS_SORT_CONFIG } from "./Table/constants";
import { buildWorkerPoolsTableRows } from "./Table/helpers";
import useWorkerPoolsTableStore from "./Table/useTableStore";
import {
  FILTERS_ORDER_SETTINGS_KEY,
  PRIVATE_WORKER_POOL_CALLOUT_STORAGE_KEY,
  VIRTUALIZATION_THRESHOLD,
} from "./constants";
import useSearchWorkerPools from "./useSearchWorkerPools";
import styles from "./styles.module.css";

const WorkerPools = () => {
  const {
    publicWorkerPool,
    entities,
    isPageEmpty,
    isPageLoading,
    loadingContent,
    notFound,
    error,
    loadMoreItems,
    hasNextPage,
    isSearchActive,
  } = useSearchWorkerPools();

  const activeRowId = useActiveDrawerId();
  const trackAnalytics = useAnalytics({ page: AnalyticsPageWorkerPool.WorkerPoolsList });

  const rows = useMemo(
    () => buildWorkerPoolsTableRows(entities, isSaasDistribution() ? publicWorkerPool : undefined),
    [entities, publicWorkerPool]
  );

  const handleCreateWorkerPool = () => {
    showWorkerPoolFormDrawer();
  };

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return (
      <WorkerPoolsPageLayout>
        <PageLayoutSkeleton />
      </WorkerPoolsPageLayout>
    );
  }

  if (notFound) {
    return <NotFoundPage />;
  }

  return (
    <WorkerPoolsPageLayout onCreate={handleCreateWorkerPool}>
      <PrivateWorkersTierInfo storageKey={PRIVATE_WORKER_POOL_CALLOUT_STORAGE_KEY} />

      <Box
        direction="column"
        grow={rows.length > 0 && !isPageEmpty ? "1" : undefined}
        className={rows.length > 0 && isPageEmpty ? styles.emptyTableWrapper : undefined}
      >
        <WorkerPoolsFiltersLayout>
          <TableSimple
            data={rows}
            loading={loadingContent}
            store={useWorkerPoolsTableStore}
            ariaLabel="Worker pools list"
            activeRowId={activeRowId}
            analyticsPage={AnalyticsPageWorkerPool.WorkerPoolsList}
            trackAnalytics={trackAnalytics}
            cellPerColumn={WORKER_POOLS_TABLE_CELLS}
            filtersStorageKey={FILTERS_ORDER_SETTINGS_KEY}
            sortConfig={WORKER_POOLS_SORT_CONFIG}
            onLoadMore={hasNextPage ? loadMoreItems : undefined}
            virtualize={entities.length >= VIRTUALIZATION_THRESHOLD}
            hasPagination={false}
          />
        </WorkerPoolsFiltersLayout>
      </Box>

      {isPageEmpty && !isSearchActive && <WorkerPoolsEmpty />}
      {isPageEmpty && isSearchActive && (
        <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
      )}
    </WorkerPoolsPageLayout>
  );
};

export default WorkerPools;
